import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";

import { Toaster } from "@/components/ui/sonner";
import { PreferencesStoreProvider } from "@/stores/preferences/preferences-provider";
import { QueryProvider } from "@/providers/query-provider";
import { ThemeProvider } from "@/providers/theme-provider";
import { getPreference } from "@/lib/cookies";
import { 
  THEME_MODE_VALUES, 
  THEME_PRESET_VALUES, 
  type ThemePreset, 
  type ThemeMode 
} from "@/types/preferences/theme";

// Helper to get system preference
function getSystemTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

// Helper to get initial theme - checks cookies first, then localStorage, then system preference
function getInitialTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'dark';
  
  // Check cookies first
  const cookieTheme = getPreference<ThemeMode>("theme_mode", THEME_MODE_VALUES);
  if (cookieTheme) return cookieTheme;
  
  // Check localStorage (used by ThemeProvider)
  const localStorageTheme = localStorage.getItem('theme_mode') as ThemeMode;
  if (localStorageTheme && THEME_MODE_VALUES.includes(localStorageTheme)) {
    return localStorageTheme;
  }
  
  // Fall back to system preference
  return getSystemTheme();
}

export function RootLayout() {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => getInitialTheme());
  const [themePreset, setThemePreset] = useState<ThemePreset>(() => 
    getPreference<ThemePreset>("theme_preset", THEME_PRESET_VALUES) || "default"
  );

  useEffect(() => {
    // Sync the initial theme to both localStorage and cookies
    localStorage.setItem('theme_mode', themeMode);
    document.cookie = `theme_mode=${themeMode}; path=/; max-age=${60 * 60 * 24 * 365}`;
    
    // Apply theme preset to HTML element
    document.documentElement.setAttribute('data-theme-preset', themePreset);
    
    // Apply theme class to HTML element
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(themeMode);
  }, [themeMode, themePreset]);

  return (
    <ThemeProvider defaultTheme={themeMode}>
      <QueryProvider>
        <PreferencesStoreProvider themeMode={themeMode} themePreset={themePreset}>
          <div className="min-h-screen antialiased">
            <Outlet />
            <Toaster />
          </div>
        </PreferencesStoreProvider>
      </QueryProvider>
    </ThemeProvider>
  );
}
